import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      logger.debug("[login] success → redirecting to /home", result);
      navigate("/");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 403 && detail === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="bg-primary flex min-h-screen items-center justify-center px-4 py-10">
      <div className="grid w-full max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-white shadow-2xl lg:grid-cols-[1.05fr_0.95fr]">
        <div className="from-primary to-primary/90 relative hidden min-h-[680px] flex-col justify-between overflow-hidden bg-linear-to-br p-10 text-white lg:flex">
          <div className="absolute inset-0">
            <div className="bg-accent/20 absolute top-20 left-10 h-40 w-40 rounded-full blur-3xl" />
            <div className="bg-accent-light absolute right-0 bottom-0 h-72 w-72 translate-x-1/4 translate-y-1/4 rounded-full blur-3xl" />
          </div>
          <div className="relative z-10">
            <img
              src="/brand/logo.png"
              alt="Board of Innovation"
              className="h-10 w-auto object-contain"
            />
          </div>
          <div className="relative z-10 max-w-lg space-y-6">
            <p className="font-heading text-accent-light text-sm tracking-[0.3em] uppercase">
              The AI Transformation Studio
            </p>
            <h1 className="font-heading text-5xl leading-none font-bold">
              Turn closed projects into reusable growth stories.
            </h1>
            <p className="text-base leading-7 text-white/78">
              Generate polished case study slides and structured Notion pages automatically from
              Slack, Drive, HubSpot, Claude, and approval workflows—built for BOI teams.
            </p>
            <div className="grid gap-3 text-sm text-white/82">
              <div className="rounded-2xl border border-white/10 bg-white/6 p-4 backdrop-blur-sm">
                Capture delivery signals, approvals, and assets the moment a project closes.
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/6 p-4 backdrop-blur-sm">
                Draft reusable narratives that mirror BOI&apos;s AI-native consulting rigor.
              </div>
            </div>
          </div>
          <div className="relative z-10 text-sm text-white/60">
            Trusted by teams building the next wave of AI transformation.
          </div>
        </div>
        <Card className="w-full rounded-none border-0 shadow-none">
          <CardHeader className="space-y-3 pt-10 text-center lg:pt-14">
            <img
              src="/brand/logo.png"
              alt="Board of Innovation"
              className="mx-auto h-10 w-auto object-contain lg:hidden"
            />
            <CardTitle className="font-heading text-primary text-3xl font-bold">
              Case Study Generator
            </CardTitle>
            <p className="text-muted text-sm">
              Sign in to build reusable proof points for every engagement.
            </p>
          </CardHeader>
          <CardContent className="px-6 pb-10 lg:px-10">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-foreground text-sm font-medium">
                  Work email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@boardofinnovation.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-foreground text-sm font-medium">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                  />
                  <label htmlFor="rememberMe" className="text-foreground text-sm">
                    Remember me
                  </label>
                </div>
                <span className="text-muted text-xs">Secure internal access</span>
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <Button type="submit" disabled={loginMutation.isPending} className="w-full">
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
              <p className="text-muted text-center text-sm">
                Don&apos;t have an account?{" "}
                <Link to="/signup" className="text-accent hover:underline">
                  Create one
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
